import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Mail, RotateCw, SlidersHorizontal, UserPlus, Users, X } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { Switch } from '@/components/ui/Switch';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { EmptyState } from '@/components/ui/EmptyState';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import {
  APP_SECTIONS,
  nextSectionAccess,
  parseSectionAccess,
  ROLE_LABELS,
  ROLES,
  sectionEnabled,
  type Role,
  type SectionAccess,
  type SectionId,
} from '@/lib/permissions';
import { formatRelative } from '@/lib/formatters';
import { useAuth } from '@/app/AuthProvider';
import {
  usePendingInvites,
  useProfiles,
  useRevokeInvite,
  useSendInvite,
  useUpdateProfileActive,
  useUpdateProfileRole,
  useUpdateProfileSectionAccess,
  type UserInvite,
} from '@/features/users/hooks';
import type { Tables } from '@/types/database';

const ROLE_BADGE: Record<Role, 'default' | 'success' | 'info' | 'muted' | 'outline'> = {
  super_admin: 'default',
  admin: 'success',
  manager: 'info',
  employee: 'muted',
  external_lender: 'outline',
};

export function RoleManagementPage() {
  const { profile: currentProfile } = useAuth();
  const { data: profiles, isLoading } = useProfiles();
  const updateRole = useUpdateProfileRole();
  const updateActive = useUpdateProfileActive();
  const updateSections = useUpdateProfileSectionAccess();

  const [confirmTarget, setConfirmTarget] = useState<{ id: string; name: string; nextRole: Role } | null>(null);
  const [sectionTarget, setSectionTarget] = useState<Tables<'profiles'> | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const onChangeRole = (id: string, name: string, nextRole: Role) => {
    if (id === currentProfile?.id) {
      toast.error("You can't change your own role.");
      return;
    }
    setConfirmTarget({ id, name, nextRole });
  };

  const confirmRoleChange = async () => {
    if (!confirmTarget) return;
    try {
      await updateRole.mutateAsync({ id: confirmTarget.id, role: confirmTarget.nextRole });
      toast.success(`Updated to ${ROLE_LABELS[confirmTarget.nextRole]}`);
      setConfirmTarget(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Update failed';
      toast.error(msg);
    }
  };

  const onToggleActive = async (id: string, is_active: boolean) => {
    if (id === currentProfile?.id) {
      toast.error("You can't deactivate yourself.");
      return;
    }
    try {
      await updateActive.mutateAsync({ id, is_active });
      toast.success(is_active ? 'User activated' : 'User deactivated');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Update failed';
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Roles & users"
        actions={
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4" />
            Invite user
          </Button>
        }
      />

      <PendingInvitesCard />

      <Card>
        <CardHeader>
          <CardTitle>People</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (profiles ?? []).length === 0 ? (
            <EmptyState icon={Users} title="No users yet" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Sections</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(profiles ?? []).map((p) => {
                  const isSelf = p.id === currentProfile?.id;
                  const initials = (p.full_name ?? p.email)
                    .split(/[.\s@]/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((s) => s[0]?.toUpperCase())
                    .join('');
                  return (
                    <TableRow key={p.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            {p.avatar_url && <AvatarImage src={p.avatar_url} alt={p.email} />}
                            <AvatarFallback>{initials}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {p.full_name ?? p.email} {isSelf && <span className="text-muted-foreground">(you)</span>}
                            </p>
                            <p className="text-xs text-muted-foreground">{p.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={p.role}
                          onValueChange={(v) => onChangeRole(p.id, p.full_name ?? p.email, v as Role)}
                          disabled={isSelf || updateRole.isPending}
                        >
                          <SelectTrigger className="w-44">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLES.map((r) => (
                              <SelectItem key={r} value={r}>
                                {ROLE_LABELS[r]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSectionTarget(p)}
                          disabled={p.role === 'external_lender'}
                        >
                          <SlidersHorizontal className="h-3.5 w-3.5" />
                          {sectionSummary(p.role, parseSectionAccess(p.section_access))}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={p.is_active}
                          onCheckedChange={(v) => onToggleActive(p.id, v)}
                          disabled={isSelf || updateActive.isPending}
                        />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {formatRelative(p.created_at)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!confirmTarget} onOpenChange={(o) => !o && setConfirmTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm role change</DialogTitle>
            <DialogDescription>
              Change <strong>{confirmTarget?.name}</strong>'s role to{' '}
              <strong>{confirmTarget && ROLE_LABELS[confirmTarget.nextRole]}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmTarget(null)}>
              Cancel
            </Button>
            <Button onClick={confirmRoleChange} disabled={updateRole.isPending}>
              {updateRole.isPending ? 'Updating…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />

      <SectionAccessSheet
        person={sectionTarget}
        pending={updateSections.isPending}
        onClose={() => setSectionTarget(null)}
        onToggle={async (sectionId, enabled) => {
          if (!sectionTarget) return;
          const current = parseSectionAccess(sectionTarget.section_access);
          const next = nextSectionAccess(current, sectionTarget.role, sectionId, enabled);
          try {
            const updated = await updateSections.mutateAsync({
              id: sectionTarget.id,
              section_access: next,
            });
            setSectionTarget(updated);
            toast.success(
              `${APP_SECTIONS.find((s) => s.id === sectionId)?.label ?? 'Section'} ${
                enabled ? 'enabled' : 'disabled'
              }. They’ll see it after they refresh Atlas.`,
            );
          } catch (e) {
            const msg = e instanceof Error ? e.message : 'Update failed';
            toast.error(msg);
          }
        }}
      />
    </div>
  );
}

const EMPTY_INVITE = { email: '', full_name: '', role: 'employee' as Role, note: '' };

function InviteUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const sendInvite = useSendInvite();
  const [form, setForm] = useState(EMPTY_INVITE);

  const close = (next: boolean) => {
    if (!next) setForm(EMPTY_INVITE);
    onOpenChange(next);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await sendInvite.mutateAsync({
        email: form.email,
        role: form.role,
        full_name: form.full_name,
        note: form.note,
      });
      toast.success(`Invite sent to ${form.email.trim()}`);
      close(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invite failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Invite user</DialogTitle>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              required
              autoFocus
              placeholder="name@virrolife.com"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invite-name">Full name (optional)</Label>
            <Input
              id="invite-name"
              placeholder="Nick Prueter"
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invite-role">Role</Label>
            <Select
              value={form.role}
              onValueChange={(v) => setForm((f) => ({ ...f, role: v as Role }))}
            >
              <SelectTrigger id="invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="invite-note">Personal note (optional)</Label>
            <Textarea
              id="invite-note"
              rows={3}
              placeholder="Welcome aboard! Start with the Dashboard and Projects…"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={sendInvite.isPending || !form.email.trim()}>
              <Mail className="h-4 w-4" />
              {sendInvite.isPending ? 'Sending…' : 'Send invite'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PendingInvitesCard() {
  const { data: invites } = usePendingInvites();
  const sendInvite = useSendInvite();
  const revokeInvite = useRevokeInvite();

  if (!invites || invites.length === 0) return null;

  const onResend = async (invite: UserInvite) => {
    try {
      await sendInvite.mutateAsync({ invite_id: invite.id });
      toast.success(`Invite re-sent to ${invite.email}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Resend failed');
    }
  };

  const onRevoke = async (invite: UserInvite) => {
    try {
      await revokeInvite.mutateAsync(invite.id);
      toast.success(`Invite for ${invite.email} revoked`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Revoke failed');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending invites</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invitee</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Last sent</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invites.map((invite) => (
              <TableRow key={invite.id}>
                <TableCell>
                  <p className="text-sm font-medium text-foreground">
                    {invite.full_name ?? invite.email}
                  </p>
                  {invite.full_name && (
                    <p className="text-xs text-muted-foreground">{invite.email}</p>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={ROLE_BADGE[invite.role]}>{ROLE_LABELS[invite.role]}</Badge>
                </TableCell>
                <TableCell className="text-xs">
                  {invite.last_send_error ? (
                    <span className="text-destructive" title={invite.last_send_error}>
                      Email failed — resend
                    </span>
                  ) : invite.last_sent_at ? (
                    <span className="text-muted-foreground">
                      {formatRelative(invite.last_sent_at)}
                      {invite.sent_count > 1 && ` · sent ${invite.sent_count}×`}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Not sent</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onResend(invite)}
                      disabled={sendInvite.isPending}
                    >
                      <RotateCw className="h-3.5 w-3.5" />
                      Resend
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRevoke(invite)}
                      disabled={revokeInvite.isPending}
                    >
                      <X className="h-3.5 w-3.5" />
                      Revoke
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function sectionSummary(role: Role, access: SectionAccess): string {
  if (role === 'super_admin') return 'All';
  if (role === 'external_lender') return 'Loans only';
  const grantable = APP_SECTIONS.filter((s) => !s.locked);
  const on = grantable.filter((s) => sectionEnabled(role, s.id, access)).length;
  return `${on}/${grantable.length}`;
}

function SectionAccessSheet({
  person,
  pending,
  onClose,
  onToggle,
}: {
  person: Tables<'profiles'> | null;
  pending: boolean;
  onClose: () => void;
  onToggle: (sectionId: SectionId, enabled: boolean) => Promise<void>;
}) {
  const access = person ? parseSectionAccess(person.section_access) : {};
  const lockedToRole = person?.role === 'super_admin' || person?.role === 'external_lender';

  return (
    <Sheet open={!!person} onOpenChange={(open) => !open && onClose()}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Section access</SheetTitle>
        </SheetHeader>
        <SheetBody className="space-y-3">
          {lockedToRole && (
            <p className="rounded-md border border-border bg-secondary/60 px-3 py-2 text-sm text-muted-foreground">
              {person?.role === 'super_admin'
                ? 'Super admins always have every section. Change their role to limit access.'
                : 'External lenders only see the loans they funded. Section grants don’t apply.'}
            </p>
          )}
          {APP_SECTIONS.map((section) => {
            const enabled = person
              ? sectionEnabled(person.role, section.id, access)
              : false;
            const disabled = !person || pending || lockedToRole || section.locked;
            return (
              <label
                key={section.id}
                className="flex items-start justify-between gap-4 rounded-md border border-border px-3 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{section.label}</p>
                </div>
                <Switch
                  checked={enabled}
                  disabled={disabled}
                  onCheckedChange={(value) => {
                    void onToggle(section.id, value);
                  }}
                />
              </label>
            );
          })}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
